import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, LifeBuoy, Loader2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("As senhas não coincidem");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <LifeBuoy className="h-6 w-6 text-indigo-400" />
          <span data-testid="brand-text" className="font-semibold text-lg text-zinc-100">Help Desk</span>
        </div>
        {done ? (
          <div data-testid="reset-success">
            <div className="h-12 w-12 rounded-full bg-emerald-400/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            </div>
            <h2 className="text-xl tracking-tight font-medium text-zinc-100 mb-2">Senha redefinida</h2>
            <p className="text-sm text-zinc-400">Redirecionando para o login…</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <h2 className="text-xl tracking-tight font-medium text-zinc-100 mb-1">Nova senha</h2>
            <p className="text-sm text-zinc-400 mb-6">Defina uma nova senha para sua conta.</p>
            {error && <div data-testid="reset-error" className="text-rose-400 bg-rose-400/10 text-sm rounded-md px-3 py-2 mb-4">{error}</div>}
            <div className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-xs text-zinc-400">Nova senha</Label>
                <Input id="password" data-testid="reset-password-input" type="password" required minLength={6}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-xs text-zinc-400">Confirmar senha</Label>
                <Input id="confirm" data-testid="reset-confirm-input" type="password" required minLength={6}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
              </div>
              <Button data-testid="reset-submit-button" type="submit" disabled={loading || !token}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redefinir senha"}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-xs text-zinc-400 hover:text-indigo-400">Voltar ao login</Link>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
